import { Dialog, DialogContent } from "@/components/ui/dialog";
import useAuth from "@/hooks/auth";
import { getStartupDetailSimple } from "@/services/api/Startup";
import { checkEmailDomainWithList as checkEmailDomain } from "@/utils/checkEmailDomain";
import { CircleNotch, Eye, EyeSlash } from "@phosphor-icons/react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useContext, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import GoogleSignInButton from "../auth/GoogleSignInButton";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

import AppContext from "@/AppContext";
import { loginPopup, registerPopup } from "@/Atoms";
import {
  sendTokenNotUser,
  verifyTokenForUserRegister,
} from "@/services/api/Master";
import { useAtom } from "jotai";
import ErrorMessage from "../ErrorMessage";
import InputTokenForAccountRegister from "./InputTokenForAccountRegister";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Drawer, DrawerContent } from "../ui/drawer";

const Login = ({ preventClose = false }) => {
  const [openLoginPopup, setOpenLoginPopup] = useAtom(loginPopup);
  const [openRegisterPopup, setOpenRegisterPopup] = useAtom(registerPopup);
  const [isSendingVerifyEmail, setIsSendingVerifyEmail] = useState(false);
  const [emailVerificationLoading, setEmailVerificationLoading] =
    useState(false);
  const [emailConfirmLoading, setEmailConfirmLoading] = useState(false);
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [startup, setStartup] = useState(null);
  const [errors, setErrors] = useState([]);
  const [confirmToken, setConfirmToken] = useState("");
  const [sendEmailVerificationLoading, setSendEmailVerificationLoading] =
    useState(false);
  const [emailMatchesWebsite, setEmailMatchesWebsite] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const { user, login, isLoading, loginError } = useAuth({
    middleware: "guest",
    redirectIfAuthenticated: false,
  });

  const { query } = router;
  const type = query.purpose;
  const startup_id = query.startup_id;

  const context = useContext(AppContext);
  const { planSubscription, loading, refreshUserSubscription } = context.state;

  const getStartup = async (id) => {
    const response = await getStartupDetailSimple(id);
    if (response.data) {
      setStartup(response.data);
    }
  };

  const emailInputRef = useRef();

  const onVerifyTokenUserRegister = async (e) => {
    e.preventDefault();
    if (!confirmToken) toast.error("Token is required!");
    setEmailVerificationLoading(true);
    setEmailConfirmLoading(true);
    try {
      const response = await verifyTokenForUserRegister({
        token: confirmToken,
        email,
      });
      try {
        await login({ email, password, setErrors, notRedirect: true });
        location.reload();
      } catch (error) {
        console.log(error);
      }
    } catch (error) {
      toast.error("Code is invalid or expired.");
    }
    setEmailVerificationLoading(false);
    setEmailConfirmLoading(false);
  };

  const onSendToken = async (e) => {
    if (!email) {
      return toast.error("Email is required!");
    }

    const isEmailMatching = await checkAndUpdateEmailDomain(
      email,
      startup?.website
    );
    if (startup && !isEmailMatching) {
      return toast.error("The email must match the startup website!");
    }

    setEmailVerificationLoading(true);
    try {
      const response = await sendTokenNotUser({ email });
      toast.success(
        `Verification token just sent to ${email}, use that token to verify the email.`
      );
      setIsSendingVerifyEmail(true);
    } catch (error) {}
    setEmailVerificationLoading(false);
  };

  const onResendToken = async (e) => {
    if (!email) {
      return toast.error("Email is required!");
    }

    const isEmailMatching = await checkAndUpdateEmailDomain(
      email,
      startup?.website
    );
    if (startup && !isEmailMatching) {
      return toast.error("The email must match the startup website!");
    }

    setSendEmailVerificationLoading(true);
    try {
      const response = await sendTokenNotUser({ email });
      toast.success(
        `Verification token just sent to ${email}, use that token to verify the email.`
      );
      setIsSendingVerifyEmail(true);
    } catch (error) {}
    setSendEmailVerificationLoading(false);
  };

  const checkAndUpdateEmailDomain = async (email, website) => {
    if (email && website) {
      const result = await checkEmailDomain(email, website);
      setEmailMatchesWebsite(result);
      return result;
    }
    return true;
  };

  useEffect(() => {
    checkAndUpdateEmailDomain(email, startup?.website);
  }, [email, startup?.website]);

  useEffect(() => {
    if (type === "register" || type === "claim") {
      if (startup_id) {
        getStartup(startup_id);
      } else {
        const startupName = query.startup_name;
        const startupWebsite = query.startup_website;
        if (startupName && startupWebsite) {
          setStartup({
            name: startupName,
            website: startupWebsite,
          });
        }
      }
    }
    emailInputRef.current?.focus();
  }, []);

  function switchPopup() {
    setOpenLoginPopup(false);
    setTimeout(() => {
      setOpenRegisterPopup(true);
    }, 50);
  }

  const submit = async function (evt) {
    evt.preventDefault();
    if (startup) {
      const isEmailMatching = await checkAndUpdateEmailDomain(
        email,
        startup.website
      );
      if (!isEmailMatching) {
        return toast.error("The email must match the startup website!");
      }
    }
    await login({
      email,
      password,
      setErrors,
      startup,
      purpose: type,
    });
    refreshUserSubscription();
  };

  // Determine the handler based on the prop
  const handleOpenChange = preventClose
    ? (open) => {
        if (open) setOpenLoginPopup(true);
      } // Only allow opening
    : setOpenLoginPopup; // Allow opening and closing

  const handleInteractOutside = preventClose
    ? (e) => e.preventDefault()
    : undefined; // Default behavior

  const handleEscapeKeyDown = preventClose
    ? (e) => e.preventDefault()
    : undefined; // Default behavior

  const isOpen = isSendingVerifyEmail ? true : openLoginPopup;

  const Content = (
    <div className='bg-white text-background'>
      {isSendingVerifyEmail ? (
        <InputTokenForAccountRegister
          {...{
            isVisible: isOpen,
            onVerifyTokenUserRegister,
            emailVerificationLoading,
            confirmToken,
            setConfirmToken,
            isLoading,
            sendEmailVerificationLoading,
            onSendTokenForAccountRegistration: onResendToken,
          }}
        />
      ) : (
        <div className='pt-5 lg:pt-0'>
          <p className='text-4xl font-semibold tracking-tight lg:text-5xl font-display'>
            Hello there! 👋
          </p>
          <p className='mt-1 text-sm lg:mt-3 lg:text-base opacity-60'>
            Please login to continue accessing our content
          </p>
          <form onSubmit={submit} className='grid grid-cols-12 gap-4 mt-5'>
            {/* Email field */}
            <div className='col-span-12 space-y-1.5 text-left'>
              <Label className='text-sm' htmlFor='email'>
                Email <span className='text-red-500'>*</span>
              </Label>
              <Input
                ref={emailInputRef}
                type='email'
                id='email'
                required
                onInput={(evt) => setEmail(evt.target.value)}
                className='border-gray-300 shadow-sm bg-white/5 hover:bg-white/10 focus-visible:ring-0'
              />
              {loginError && (
                <div className='flex items-center justify-between'>
                  <ErrorMessage message={loginError} />
                  {loginError === "Please verify your email first!" && (
                    <div
                      className='text-xs text-blue-600 cursor-pointer'
                      onClick={onSendToken}
                    >
                      {emailVerificationLoading ? (
                        <p className='animate-pulse text-slate-500'>
                          Sending verification code...
                        </p>
                      ) : (
                        "Verify Email"
                      )}
                    </div>
                  )}
                </div>
              )}
              {startup && !emailMatchesWebsite && (
                <small className='text-red-500'>
                  This email does not match the startup website!
                </small>
              )}
            </div>

            {/* Password field */}
            <div className='col-span-12 space-y-1.5 text-left'>
              <Label className='text-sm' htmlFor='password'>
                Password <span className='text-red-500'>*</span>
              </Label>
              <div className='relative'>
                <Input
                  type={showPassword ? "text" : "password"}
                  id='password'
                  required
                  onInput={(evt) => setPassword(evt.target.value)}
                  className='border-gray-300 shadow-sm bg-white/5 hover:bg-white/10 focus-visible:ring-0'
                />
                <button
                  type='button'
                  onClick={() => setShowPassword(!showPassword)}
                  className='absolute z-10 p-1 transition-colors -translate-y-1/2 right-3 top-1/2 text-black/60 hover:text-black'
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeSlash size={20} className='text-current' />
                  ) : (
                    <Eye size={20} className='text-current' />
                  )}
                </button>
              </div>
            </div>

            {/* Forgot password */}
            <div className='flex items-center justify-end col-span-12'>
              <Link legacyBehavior href={"/forgot-password"} passHref>
                <a className='inline-flex items-center text-sm font-medium underline duration-300 hover:opacity-70'>
                  Forgot Password
                </a>
              </Link>
            </div>

            {/* Submit + optional Google Sign-in */}
            <div className='flex flex-col items-center col-span-12 gap-4'>
              <Button
                type='submit'
                variant='secondary'
                className='w-full'
                disabled={isLoading}
              >
                {isLoading && (
                  <CircleNotch
                    weight='bold'
                    className='mr-2 text-lg animate-spin'
                  />
                )}
                {isLoading ? "Processing..." : "Login"}
              </Button>
              {!startup && (
                <div className='flex flex-col items-center w-full gap-4'>
                  <div className='flex items-center w-full -my-1 space-x-3'>
                    <span className='flex-1 h-px bg-background/10'></span>
                    <span className='text-[10px] uppercase font-medium tracking-wider'>
                      or
                    </span>
                    <span className='flex-1 h-px bg-background/10'></span>
                  </div>
                  <GoogleSignInButton />
                </div>
              )}
            </div>
          </form>
          <div className='flex justify-center'>
            <button
              type='button'
              onClick={switchPopup}
              className='mt-4 text-xs font-medium underline opacity-60'
            >
              {`I don't have an account`}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const isMobile = useIsMobile();

  return !isMobile ? (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className='p-8 bg-white !rounded-2xl'
        hideCloseButton
        onInteractOutside={handleInteractOutside}
        onEscapeKeyDown={handleEscapeKeyDown}
      >
        {Content}
      </DialogContent>
    </Dialog>
  ) : (
    <Drawer open={isOpen} onOpenChange={handleOpenChange}>
      <DrawerContent className='px-5 pb-5 bg-white text-background rounded-t-3xl'>
        {Content}
      </DrawerContent>
    </Drawer>
  );
};

export default Login;
